import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
function AnimatedStat({
  value,
  suffix,
  label,
  detail
}: {
  value: number;
  suffix: string;
  label: string;
  detail: string;
}) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => Math.round(v));
  useEffect(() => {
    if (isInView) {
      const controls = animate(mv, value, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, value, mv]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-342">
      <p className="text-4xl sm:text-5xl font-semibold text-carbon-bright" data-id="element-343">
        {display.toLocaleString()}
        {suffix}
      </p>
      <p className="mt-2 text-base font-bold text-carbon-deep" data-id="element-344">{label}</p>
      <p className="mt-1 text-sm text-carbon-deep leading-relaxed" data-id="element-345">{detail}</p>
    </div>;
}
export function CoffeeStats() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-346">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-347">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-348">

          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-349">
            Why coffee traceability matters
          </h2>
          <p className="mt-3 text-lg text-white/60 max-w-2xl mx-auto" data-id="element-350">
            The global coffee industry faces critical transparency challenges
            that affect millions of lives and our planet.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.12
          }
        }
      }} data-id="element-351">

          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6
            }
          }
        }} data-id="element-352">

            <AnimatedStat value={125} suffix="M+" label="People Depend on Coffee" detail="Over 125 million people worldwide rely on coffee for their livelihoods" data-id="element-353" />

          </motion.div>
          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6
            }
          }
        }} data-id="element-354">

            <AnimatedStat value={25} suffix="M+" label="Smallholder Farmers" detail="Most coffee is grown by smallholders farming less than 5 hectares" data-id="element-355" />

          </motion.div>
          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6
            }
          }
        }} data-id="element-356">

            <AnimatedStat value={5} suffix="%" label="Fully Traceable" detail="Only ~5% of global coffee supply chains have full farm-to-cup traceability" data-id="element-357" />

          </motion.div>
          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6
            }
          }
        }} data-id="element-358">

            <AnimatedStat value={10} suffix="M" label="Hectares at Risk" detail="Coffee-driven deforestation threatens millions of hectares of tropical forest" data-id="element-359" />

          </motion.div>
        </motion.div>
      </div>
    </section>;
}